import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../styles/common.css';

function ChildPoints() {
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState({ balance: 0, transactions: [] });
  const [weeklyPreview, setWeeklyPreview] = useState({ completedMissions: 0, totalMissions: 0, expectedAmount: 0 });
  const [rewardRequests, setRewardRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemForm, setRedeemForm] = useState({ amount: '', description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // 로그인 상태 확인
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/child/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.type !== 'child') {
        navigate('/');
        return;
      }
      setUser(parsedUser);
      fetchWalletData(parsedUser.id);
      fetchWeeklyPreview();
      fetchRewardRequests();
    } catch (error) {
      console.error('사용자 데이터 파싱 오류:', error);
      navigate('/child/login');
    }
  }, [navigate]);

  const fetchWalletData = async (childId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v0/wallets/${childId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWallet({
          balance: data.wallet.currentBalance || 0,
          transactions: data.transactions || []
        });
      } else {
        console.error('지갑 데이터 로딩 실패');
      }
    } catch (error) {
      console.error('지갑 데이터 로딩 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRewardRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v0/rewards/my-requests', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRewardRequests(data.requests || []);
      } else {
        console.error('선물 요청 데이터 로딩 실패');
      }
    } catch (error) {
      console.error('선물 요청 데이터 로딩 오류:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleRedeemRequest = () => {
    setShowRedeemModal(true);
  };

  const handleRedeemSubmit = async (e) => {
    e.preventDefault();

    if (!redeemForm.amount || !redeemForm.description) {
      alert('모든 항목을 입력해주세요.');
      return;
    }

    const amount = parseInt(redeemForm.amount);
    if (amount <= 0 || amount > wallet.balance) {
      alert('올바른 금액을 입력해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v0/rewards/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: amount,
          description: redeemForm.description
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert('선물 요청이 완료되었습니다! 🎁\n부모님의 승인을 기다려주세요.');
        setShowRedeemModal(false);
        setRedeemForm({ amount: '', description: '' });
        // 지갑 데이터 새로고침 (잔액은 변하지 않음)
        fetchWalletData(user.id);
      } else {
        alert(data.error || '선물 요청 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('선물 요청 오류:', error);
      alert('선물 요청 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchWeeklyPreview = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch('/api/v0/reports/child/weekly-preview', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWeeklyPreview(data.weeklyPreview);
      } else {
        console.error('주간 예상 정보 조회 실패:', response.status);
      }
    } catch (error) {
      console.error('주간 예상 정보 조회 오류:', error);
    }
  };

  const handleRedeemCancel = () => {
    setShowRedeemModal(false);
    setRedeemForm({ amount: '', description: '' });
  };

  if (!user) {
    return (
      <div className="page-container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            로딩 중...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* 헤더 */}
      <div className="page-header">
        <Link to="/child/dashboard" className="back-btn">← 뒤로가기</Link>
        <h1>💰 내 포인트</h1>
        <button onClick={handleLogout} className="btn btn-outline btn-sm">
          로그아웃
        </button>
      </div>

      <div className="page-content">
        {/* 포인트 카드 */}
        <div className="points-card">
          <div className="points-content">
            <div className="acorn-icon"><img src="/icon-512.png" alt="도토리" /></div>
            <div className="points-info">
              <h2>내 도토리</h2>
              <p className="points-amount">{wallet.balance}개</p>
            </div>
          </div>
        </div>

        {/* 이번 주 예상 */}
        <div className="preview-card">
          <h3>🗓️ 이번 주 예상</h3>
          <div className="preview-content">
            <div className="preview-item">
              <span>완료한 미션</span>
              <span className="preview-value">{weeklyPreview.completedMissions}/{weeklyPreview.totalMissions}</span>
            </div>
            <div className="preview-item">
              <span>예상 도토리</span>
              <span className="preview-value">+{weeklyPreview.expectedAmount}개</span>
            </div>
          </div>
        </div>

        {/* 보상 사용 */}
        <div className="rewards-card">
          <h3>🎁 도토리 사용하기</h3>
          <p>부모님께 선물을 요청해보세요! 승인되면 도토리가 차감됩니다.</p>
          <div className="rewards-actions">
            <button
              className="btn btn-primary btn-full"
              onClick={handleRedeemRequest}
              disabled={wallet.balance === 0}
            >
              선물 요청하기
            </button>
          </div>
        </div>

        {/* 거래 내역 */}
        <div className="transactions-card">
          <h3>📋 최근 내역</h3>
          {(() => {
            // 지갑 거래와 선물 요청을 합쳐서 시간순 정렬
            const allTransactions = [
              // 지갑 거래 (승인된 선물 요청 + 정산)
              ...wallet.transactions.map(tx => ({
                ...tx,
                source: 'wallet',
                timestamp: new Date(tx.createdAt)
              })),
              // 거절된 선물 요청
              ...rewardRequests
                .filter(req => req.status === 'rejected')
                .map(req => ({
                  id: req.id,
                  type: 'rejected_request',
                  amount: req.amount,
                  description: req.description,
                  response_note: req.response_note,
                  createdAt: req.responded_at || req.requested_at,
                  source: 'request',
                  timestamp: new Date(req.responded_at || req.requested_at)
                }))
            ].sort((a, b) => b.timestamp - a.timestamp);

            if (allTransactions.length === 0) {
              return (
                <div className="empty-state">
                  <div className="empty-icon">🌰</div>
                  <p>아직 거래 내역이 없어요</p>
                  <p className="empty-subtitle">미션을 완료하면 도토리를 받을 수 있어요!</p>
                </div>
              );
            }

            return (
              <div className="transactions-list">
                {allTransactions.map((transaction, index) => {
                const getTransactionIcon = (type, referenceType, description) => {
                  if (type === 'earn') return '🌟'; // 미션 완료
                  if (type === 'bonus') return '🎉'; // 보너스
                  if (type === 'spend' && referenceType === 'redeem') return '🎁'; // 선물 요청
                  if (type === 'spend' && !description.includes('정산')) return '🎁'; // 임시: 정산이 아닌 spend는 선물 요청으로 간주
                  if (type === 'penalty') return '😔'; // 벌점
                  if (type === 'rejected_request') return '❌'; // 거절된 선물 요청
                  return '💰'; // 기본
                };

                const getTransactionTitle = (type, referenceType, description) => {
                  if (type === 'earn') return '미션 완료로 도토리 받음';
                  if (type === 'bonus') return '보너스 도토리 받음';
                  if (type === 'spend' && referenceType === 'redeem') return `선물 요청: ${description}`;
                  if (type === 'spend' && !description.includes('정산')) return `선물 요청: ${description}`; // 임시 해결책
                  if (type === 'penalty') return '벌점으로 도토리 차감';
                  if (type === 'rejected_request') return `선물 요청: ${description}`;
                  return description;
                };

                const getTransactionSubtitle = (type, referenceType, description, responseNote) => {
                  if (type === 'earn') return '잘했어요! 👏';
                  if (type === 'bonus') return '특별 보상!';
                  if (type === 'spend' && referenceType === 'redeem') return '부모님이 OK 해주셨어요!';
                  if (type === 'spend' && !description.includes('정산')) return responseNote ? `부모님이 OK 해주셨어요! (${responseNote})` : '부모님이 OK 해주셨어요!';
                  if (type === 'penalty') return '다음엔 더 잘해봐요';
                  if (type === 'rejected_request') return responseNote ? `지금은 안 돼요 (${responseNote})` : '지금은 안 돼요';
                  return '';
                };

                return (
                  <div key={transaction.id || index} className="transaction-item">
                    <div className="transaction-icon">
                      {getTransactionIcon(transaction.type, transaction.reference_type, transaction.description)}
                    </div>
                    <div className="transaction-info">
                      <h4>{getTransactionTitle(transaction.type, transaction.reference_type, transaction.description)}</h4>
                      <p className="transaction-subtitle">{getTransactionSubtitle(transaction.type, transaction.reference_type, transaction.description, transaction.response_note)}</p>
                      <p className="transaction-date">{new Date(transaction.createdAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}</p>
                    </div>
                    <div className={`transaction-amount ${
                      transaction.type === 'rejected_request' ? 'neutral' :
                      transaction.type === 'earn' || transaction.type === 'bonus' ? 'positive' : 'negative'
                    }`}>
                      {transaction.type === 'rejected_request' ?
                        `${transaction.amount}개` :
                        `${transaction.type === 'earn' || transaction.type === 'bonus' ? '+' : '-'}${transaction.amount}개`
                      }
                    </div>
                  </div>
                );
              })}
            </div>
            );
          })()}
        </div>

      </div>

      {/* 선물 요청 모달 */}
      {showRedeemModal && (
        <div className="modal-overlay" onClick={handleRedeemCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🎁 선물 요청하기</h2>
              <button className="modal-close" onClick={handleRedeemCancel}>×</button>
            </div>
            <form onSubmit={handleRedeemSubmit} className="redeem-form">
              <div className="form-group">
                <label htmlFor="amount">사용할 도토리 개수</label>
                <input
                  id="amount"
                  type="number"
                  min="1"
                  max={wallet.balance}
                  value={redeemForm.amount}
                  onChange={(e) => setRedeemForm({ ...redeemForm, amount: e.target.value })}
                  placeholder="개수를 입력하세요"
                  className="form-input"
                />
                <small>현재 보유: {wallet.balance}개</small>
              </div>
              <div className="form-group">
                <label htmlFor="description">어떤 선물을 원하나요?</label>
                <textarea
                  id="description"
                  value={redeemForm.description}
                  onChange={(e) => setRedeemForm({ ...redeemForm, description: e.target.value })}
                  placeholder="예: 아이스크림, 문구용품, 장난감 등"
                  className="form-textarea"
                  rows="3"
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={handleRedeemCancel}
                  className="btn btn-outline"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary"
                >
                  {isSubmitting ? '요청 중...' : '요청하기'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .page-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding-bottom: 20px;
        }

        .page-header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          padding: 15px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }


        .page-header h1 {
          margin: 0;
          color: #764ba2;
          font-size: 1.3rem;
          font-weight: bold;
        }

        .page-content {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }

        .points-card {
          background: linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%);
          border-radius: 20px;
          padding: 30px;
          margin-bottom: 20px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.1);
        }

        .points-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 20px;
        }

        .acorn-icon {
          font-size: 4rem;
        }

        .acorn-icon img {
          width: 4rem;
          height: 4rem;
          object-fit: contain;
        }

        .points-info {
          text-align: center;
          color: white;
        }

        .points-info h2 {
          margin: 0 0 10px 0;
          font-size: 1.3rem;
        }

        .points-amount {
          font-size: 3rem;
          font-weight: bold;
          margin: 10px 0;
        }

        .points-subtitle {
          font-size: 1rem;
          opacity: 0.8;
          margin: 0;
        }

        .preview-card, .transactions-card, .rewards-card {
          background: white;
          border-radius: 15px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
        }

        .preview-card h3, .transactions-card h3, .rewards-card h3 {
          color: #333;
          margin-bottom: 15px;
        }

        .preview-content {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .preview-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 0;
          border-bottom: 1px solid #f0f0f0;
        }

        .preview-item:last-child {
          border-bottom: none;
        }

        .preview-value {
          font-weight: bold;
          color: #764ba2;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .empty-icon {
          font-size: 3rem;
          margin-bottom: 15px;
        }

        .empty-state p {
          margin: 8px 0;
        }

        .empty-subtitle {
          font-size: 0.9rem;
          opacity: 0.7;
        }

        .transactions-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .transaction-item {
          display: flex;
          align-items: center;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 10px;
          gap: 12px;
        }

        .transaction-icon {
          font-size: 1.5rem;
          width: 40px;
          text-align: center;
          flex-shrink: 0;
        }

        .transaction-info {
          flex: 1;
          min-width: 0;
        }

        .transaction-info h4 {
          margin: 0 0 4px 0;
          color: #333;
          font-size: 0.95rem;
          font-weight: 600;
        }

        .transaction-subtitle {
          margin: 0 0 4px 0;
          color: #8B4513;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .transaction-date {
          margin: 0;
          color: #999;
          font-size: 0.8rem;
        }

        .transaction-amount {
          font-weight: bold;
          font-size: 1.1rem;
        }

        .transaction-amount.positive {
          color: #28a745;
        }

        .transaction-amount.negative {
          color: #dc3545;
        }

        .transaction-amount.neutral {
          color: #666;
        }

        .rewards-card {
          text-align: center;
        }

        .rewards-card p {
          color: #666;
          margin-bottom: 20px;
        }

        .rewards-actions {
          margin-top: 15px;
        }

        /* 모달 스타일 */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: white;
          border-radius: 15px;
          width: 100%;
          max-width: 400px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h2 {
          margin: 0;
          color: #333;
          font-size: 1.2rem;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #999;
          padding: 0;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-close:hover {
          background: #f0f0f0;
          color: #333;
        }

        .redeem-form {
          padding: 20px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: bold;
          color: #333;
        }

        .form-input, .form-textarea {
          width: 100%;
          padding: 12px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 1rem;
          box-sizing: border-box;
          transition: border-color 0.3s;
        }

        .form-input:focus, .form-textarea:focus {
          outline: none;
          border-color: #764ba2;
        }

        .form-group small {
          display: block;
          margin-top: 5px;
          color: #666;
          font-size: 0.9rem;
        }

        .modal-actions {
          display: flex;
          gap: 10px;
          margin-top: 20px;
        }

        .modal-actions .btn {
          flex: 1;
          padding: 12px;
          font-size: 1rem;
        }

        @media (max-width: 480px) {
          .points-content {
            flex-direction: column;
            gap: 15px;
          }

          .modal-overlay {
            padding: 10px;
          }

          .modal-content {
            max-height: 95vh;
          }
        }
      `}</style>
    </div>
  );
}

export default ChildPoints;